import { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Battery, Gauge, Zap, ArrowLeft, MessageCircle, Check, MapPin, Calendar, Shield, Award } from 'lucide-react';
import { api } from '../App';

// Lazy load heavy components
const FAQSection = lazy(() => import('../components/FAQSection'));
const SalesIdentitySection = lazy(() => import('../components/SalesIdentitySection'));
const Footer = lazy(() => import('../components/Footer'));

export default function CarDetailPage() {
  const { slug } = useParams();
  const [car, setCar] = useState(null);
  const [cars, setCars] = useState([]);
  const [salesInfo, setSalesInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [carRes, carsRes, salesRes, settingsRes] = await Promise.all([
          api.get(`/cars/${slug}`),
          api.get('/cars'),
          api.get('/sales-info'),
          api.get('/admin-settings')
        ]);
        setCar(carRes.data);
        setCars(carsRes.data);
        setSalesInfo(salesRes.data);
        setSettings(settingsRes.data);
        document.title = `${carRes.data.name} - BYD Indonesia`;
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const formatWaNumber = (num) => {
    let n = (num || '082319804253').replace(/\D/g, '');
    if (n.startsWith('0')) n = '62' + n.substring(1);
    return n;
  };

  const getWaLink = (number, message) => {
    const num = formatWaNumber(number);
    const msg = encodeURIComponent(message || `Halo Bayu, saya tertarik dengan ${car?.name}`);
    return `https://wa.me/${num}?text=${msg}`;
  };

  if (loading) {
    return <div className="min-h-screen pt-20 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" /></div>;
  }

  if (!car) {
    return <div className="min-h-screen pt-20 flex flex-col items-center justify-center"><h1 className="text-2xl font-bold mb-4">Mobil Tidak Ditemukan</h1><Link to="/" className="btn-primary">Kembali</Link></div>;
  }

  // Button configs from car data
  const showHeroWaButton = car.show_hero_wa_button !== false;
  const showHeroSecondaryButton = car.show_hero_secondary_button !== false;
  const showCtaSection = car.show_cta_section !== false;
  const showCtaWaButton = car.show_cta_wa_button !== false;
  const showCtaSecondaryButton = car.show_cta_secondary_button !== false;

  const heroWaText = car.hero_wa_button_text || 'Tanya Harga & Promo';
  const heroWaNumber = car.hero_wa_button_number || salesInfo?.whatsapp_number || '082319804253';
  const heroWaMessage = car.hero_wa_button_message || `Halo Bayu, saya tertarik dengan ${car.name}`;
  const heroSecondaryText = car.hero_secondary_button_text || 'Simulasi Kredit';
  const heroSecondaryLink = car.hero_secondary_button_link || '/simulasi-kredit-byd-m6-jakarta';
  
  const ctaTitle = car.cta_title || `Tertarik dengan ${car.name}?`;
  const ctaDescription = car.cta_description || `Dapatkan penawaran terbaik dari ${salesInfo?.name || 'Bayu Akbar Bisono'}`;
  const ctaWaText = car.cta_wa_button_text || 'Chat WhatsApp Sekarang';
  const ctaWaNumber = car.cta_wa_button_number || salesInfo?.whatsapp_number || '082319804253';
  const ctaWaMessage = car.cta_wa_button_message || `Halo Bayu, saya mau tanya ${car.name}`;
  const ctaSecondaryText = car.cta_secondary_button_text || 'Hitung Simulasi Kredit';
  const ctaSecondaryLink = car.cta_secondary_button_link || '/simulasi-kredit-byd-m6-jakarta';

  return (
    <main className="pt-16 lg:pt-20" data-testid={`car-detail-${slug}`}>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] lg:min-h-[70vh] flex items-end" aria-labelledby="car-name">
        <div className="absolute inset-0" aria-hidden="true">
          <img 
            src={car.image_url} 
            alt={car.image_alt || `${car.name} - ${car.tagline}`} 
            className="w-full h-full object-cover" 
            loading="eager" 
            fetchpriority="high" 
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        </div>

        <div className="byd-container relative z-10 pb-12 lg:pb-16">
          <div className="animate-fadeInUp">
            <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors min-h-[44px]" aria-label="Kembali ke beranda"><ArrowLeft className="w-4 h-4" aria-hidden="true" />Kembali</Link>

            <div className="flex gap-2 mb-4">
              {car.is_bestseller && <span className="badge-bestseller" aria-label="Model terlaris">Terlaris</span>}
              <span className="bg-white/20 backdrop-blur-sm px-3 py-1 text-xs font-medium text-white">{car.category}</span>
            </div>

            <h1 id="car-name" className="heading-display text-4xl sm:text-5xl lg:text-6xl text-white mb-4">{car.name}</h1>
            <p className="text-xl text-white/80 mb-6">{car.tagline}</p>

            <div className="flex flex-wrap gap-4 mb-8" role="list" aria-label="Spesifikasi utama">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full" role="listitem"><Battery className="w-4 h-4 text-green-400" aria-hidden="true" /><span className="text-sm text-white">{car.battery_range}</span></div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full" role="listitem"><Gauge className="w-4 h-4 text-blue-400" aria-hidden="true" /><span className="text-sm text-white">{car.motor_power}</span></div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full" role="listitem"><Zap className="w-4 h-4 text-yellow-400" aria-hidden="true" /><span className="text-sm text-white">{car.charging_time}</span></div>
            </div>

            <div className="flex flex-wrap gap-4">
              {showHeroWaButton && (
                <a 
                  href={getWaLink(heroWaNumber, heroWaMessage)} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-hero-primary btn-pulse flex items-center gap-2" 
                  data-testid="car-hero-wa-btn"
                  aria-label={`${heroWaText} via WhatsApp`}
                  onClick={(e) => {
                    // Google Ads conversion tracking for BYD Sealion 7
                    if (slug === 'byd-sealion-7' && typeof window.gtag_report_conversion_sealion7 === 'function') {
                      e.preventDefault();
                      window.gtag_report_conversion_sealion7(getWaLink(heroWaNumber, heroWaMessage));
                    }
                    // Google Ads conversion tracking for DENZA D9
                    else if (slug === 'byd-denza-d9' && typeof window.gtag_report_conversion === 'function') {
                      e.preventDefault();
                      window.gtag_report_conversion(getWaLink(heroWaNumber, heroWaMessage));
                    }
                  }}
                >
                  <MessageCircle className="w-5 h-5" aria-hidden="true" />{heroWaText}
                </a>
              )}
              {showHeroSecondaryButton && (
                <Link to={heroSecondaryLink} className="btn-hero-secondary min-h-[48px] flex items-center" data-testid="car-hero-secondary-btn" aria-label={heroSecondaryText}>
                  {heroSecondaryText}
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Price & Quick Info */}
      <section className="bg-slate-900 text-white py-8">
        <div className="byd-container">
          <div className="grid md:grid-cols-4 gap-6">
            <div><span className="text-slate-400 text-sm">Harga Mulai</span><p className="text-2xl font-bold">{car.price_range || car.price}</p></div>
            <div><span className="text-slate-400 text-sm">Jarak Tempuh</span><p className="text-2xl font-bold">{car.battery_range}</p></div>
            <div><span className="text-slate-400 text-sm">Tenaga Motor</span><p className="text-2xl font-bold">{car.motor_power}</p></div>
            <div><span className="text-slate-400 text-sm">Fast Charging</span><p className="text-2xl font-bold">{car.charging_time}</p></div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="section-spacing">
        <div className="byd-container">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="animate-fadeInUp">
              <span className="text-label mb-4 block">Tentang {car.name}</span>
              <h2 className="heading-display text-3xl sm:text-4xl text-slate-900 mb-6">{car.tagline}</h2>
              <p className="text-slate-600 leading-relaxed mb-8">{car.description}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {car.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-100">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" aria-hidden="true" /><span className="text-sm text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="animate-fadeInUp" style={{animationDelay: '0.2s'}}>
              <img src={car.image_url} alt={car.image_alt || car.name} className="w-full aspect-video object-cover" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-spacing bg-slate-50">
        <div className="byd-container">
          <div className="text-center mb-12 animate-fadeInUp">
            <span className="text-label mb-4 block">Keunggulan</span>
            <h2 className="heading-display text-3xl sm:text-4xl text-slate-900">{car.benefits_title || `Mengapa ${car.name}?`}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-8 border border-slate-200 animate-fadeInUp" style={{animationDelay: '0.1s'}}>
              <Shield className="w-10 h-10 text-blue-600 mb-4" aria-hidden="true" /><h3 className="text-lg font-bold text-slate-900 mb-2">Garansi 8 Tahun Baterai</h3><p className="text-sm text-slate-600">Blade Battery dilindungi garansi 8 tahun atau 150.000 km.</p>
            </div>
            <div className="bg-white p-8 border border-slate-200 animate-fadeInUp" style={{animationDelay: '0.2s'}}>
              <Award className="w-10 h-10 text-green-600 mb-4" aria-hidden="true" /><h3 className="text-lg font-bold text-slate-900 mb-2">Biaya Operasional Rendah</h3><p className="text-sm text-slate-600">Hemat hingga 70% biaya BBM.</p>
            </div>
            <div className="bg-white p-8 border border-slate-200 animate-fadeInUp" style={{animationDelay: '0.3s'}}>
              <Calendar className="w-10 h-10 text-purple-600 mb-4" aria-hidden="true" /><h3 className="text-lg font-bold text-slate-900 mb-2">Test Drive Gratis</h3><p className="text-sm text-slate-600">Jadwalkan test drive gratis di dealer atau rumah Anda.</p>
            </div>
          </div>
        </div>
      </section>

      <Suspense fallback={<div className="py-12 text-center"><div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full mx-auto" /></div>}>
        <FAQSection carSlug={slug} title={`FAQ ${car.name}`} settings={settings} />
        <SalesIdentitySection salesInfo={salesInfo} />
      </Suspense>

      {/* CTA Section */}
      {showCtaSection && (
        <section className="bg-gradient-to-br from-[#00529B] via-[#003d73] to-[#002855] text-white py-20" data-testid="car-cta-section">
          <div className="byd-container text-center">
            <div className="animate-fadeInUp">
              <h2 className="heading-display text-3xl sm:text-4xl lg:text-5xl mb-4">{ctaTitle}</h2>
              <p className="text-white/80 mb-10 max-w-2xl mx-auto text-lg">{ctaDescription}</p>
              <div className="flex flex-wrap justify-center gap-4">
                {showCtaWaButton && (
                  <a 
                    href={getWaLink(ctaWaNumber, ctaWaMessage)} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn-cta-wa flex items-center gap-3" 
                    data-testid="car-cta-wa-btn" 
                    aria-label={`${ctaWaText} via WhatsApp`}
                    onClick={(e) => {
                      // Google Ads conversion tracking for BYD Sealion 7
                      if (slug === 'byd-sealion-7' && typeof window.gtag_report_conversion_sealion7 === 'function') {
                        e.preventDefault();
                        window.gtag_report_conversion_sealion7(getWaLink(ctaWaNumber, ctaWaMessage));
                      }
                      // Google Ads conversion tracking for DENZA D9
                      else if (slug === 'byd-denza-d9' && typeof window.gtag_report_conversion === 'function') {
                        e.preventDefault();
                        window.gtag_report_conversion(getWaLink(ctaWaNumber, ctaWaMessage));
                      }
                    }}
                  >
                    <MessageCircle className="w-6 h-6" aria-hidden="true" />{ctaWaText}
                  </a>
                )}
                {showCtaSecondaryButton && (
                  <Link to={ctaSecondaryLink} className="btn-hero-secondary px-8 py-4" data-testid="car-cta-secondary-btn">
                    {ctaSecondaryText}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <Suspense fallback={null}>
        <Footer settings={settings} salesInfo={salesInfo} cars={cars} />
      </Suspense>
    </main>
  );
}
